import { observer } from 'mobx-react-lite';
import Popup from '../../../../components/common-components/pop-up/Popup';
import CommonButton from '../../../../components/common-components/button/CommonButton';
import footerStyles from '../../../../components/form-components/common-components/FormPopupFooter.module.css';
import { useStore } from '../../../../hooks/useStore';
import styles from './AppointmentDetailPopup.module.css';
import moment from 'moment';
import 'moment/locale/tr';
import { Calendar, Navigation2 } from 'lucide-react';

moment.locale('tr');

const AppointmentDetailPopup = () => {
  const { popupStore, appointmentStore } = useStore();
  const appointment = appointmentStore.selectedAppointmentToView;

  const handleClose = () => {
    popupStore.setIsAppointmentDetailOpen(false);
    appointmentStore.setSelectedAppointmentToView(null);
  };

  if (!appointment) {
    return null;
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return moment(dateStr).format('DD.MM.YYYY');
  };

  const formatDateTime = (dateStr?: string, startTime?: string, endTime?: string) => {
    if (!dateStr || !startTime) return '-';
    const start = moment(`${dateStr} ${startTime}`, 'YYYY-MM-DD HH:mm').format('DD.MM.YYYY HH:mm');
    if (endTime) {
      const end = moment(`${dateStr} ${endTime}`, 'YYYY-MM-DD HH:mm').format('HH:mm');
      return `${start} - ${end}`;
    }
    return start;
  };

  const handleOpenGoogleMaps = () => {
    if (appointment?.hospitalLocation) {
      const encodedLocation = encodeURIComponent(appointment.hospitalLocation);
      const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodedLocation}`;
      window.open(googleMapsUrl, '_blank');
    }
  };

  const hasLocation = appointment?.hospitalLocation && appointment.hospitalLocation.trim() !== '';

  return (
    <Popup
      isOpen={popupStore.isAppointmentDetailOpen}
      onClose={handleClose}
      title="Randevu Detayları"
      description="Randevu detaylarını görüntüleyin"
      variant="info"
      icon={<Calendar size={22} strokeWidth={2} />}
      footer={
        <div className={footerStyles.actions}>
          <CommonButton
            type="outline"
            className={footerStyles.cancelBtn}
            onClick={handleClose}
          >
            Kapat
          </CommonButton>
        </div>
      }
    >
      <div className={styles.container}>
        <ul className={styles.detailList}>
          <li className={styles.detailItem}>
            <span className={styles.detailLabel}>Kişi Adı</span>
            <span className={styles.detailValue}>{appointment.personName || '—'}</span>
          </li>
          <li className={styles.detailItem}>
            <span className={styles.detailLabel}>Etiket Numarası</span>
            <span className={styles.detailValue}>{appointment.requestNumber || '—'}</span>
          </li>
          <li className={styles.detailItem}>
            <span className={styles.detailLabel}>Hastane</span>
            <span className={styles.detailValue}>{appointment.hospitalName || '—'}</span>
          </li>
          <li className={styles.detailItem}>
            <span className={styles.detailLabel}>Hastane Konumu</span>
            <div className={styles.locationRow}>
              <span className={styles.detailValue}>{appointment.hospitalLocation || '—'}</span>
              {hasLocation ? (
                <button
                  type="button"
                  onClick={handleOpenGoogleMaps}
                  className={styles.mapButton}
                  title="Konuma Git"
                >
                  <Navigation2 size={16} strokeWidth={2} />
                  Konuma Git
                </button>
              ) : null}
            </div>
          </li>
          <li className={styles.detailItem}>
            <span className={styles.detailLabel}>Randevu Tarihi</span>
            <span className={styles.detailValue}>{formatDate(appointment.appointmentDate)}</span>
          </li>
          <li className={styles.detailItem}>
            <span className={styles.detailLabel}>Saat Aralığı</span>
            <span className={styles.detailValue}>
              {appointment.appointmentStartTime && appointment.appointmentEndTime
                ? `${appointment.appointmentStartTime} - ${appointment.appointmentEndTime}`
                : '—'}
            </span>
          </li>
          <li className={styles.detailItem}>
            <span className={styles.detailLabel}>Tarih ve Saat</span>
            <span className={styles.detailValue}>
              {formatDateTime(
                appointment.appointmentDate,
                appointment.appointmentStartTime,
                appointment.appointmentEndTime
              )}
            </span>
          </li>
          <li className={styles.detailItem}>
            <span className={styles.detailLabel}>Kullanıcı</span>
            <span className={styles.detailValue}>
              {appointment.userName || appointment.userId || '—'}
            </span>
          </li>
          <li className={styles.detailItem}>
            <span className={styles.detailLabel}>Cihaz Listesi</span>
            <span className={styles.detailValue}>{appointment.deviceList || '—'}</span>
          </li>
        </ul>
      </div>
    </Popup>
  );
};

export default observer(AppointmentDetailPopup);
